import { format, parseISO } from "date-fns";
import ReactECharts from "echarts-for-react";
import PropTypes from "prop-types";
import React, { useMemo } from "react";

const TEMP_COLORS = {
  central: "#FF6B6B", // Red for central (body core)
  distal: "#4ECDC4", // Teal for distal (finger tip)
  proximal: "#9B59B6", // Purple for proximal (near palm)
};

const DATE_FORMATS = {
  week: "dd/MM",
  month: "dd/MM",
  "6month": "MMM",
  year: "MMM",
  default: "HH:mm",
};

const getDateFormat = (range) => DATE_FORMATS[range] || DATE_FORMATS.default;

function formatChartData(dataPoints, range) {
  const labels = [];
  const centralData = [];
  const distalData = [];
  const proximalData = [];

  (dataPoints || []).forEach((item) => {
    const label = format(parseISO(item.timestamp), getDateFormat(range));
    labels.push(label);
    centralData.push(item.central);
    distalData.push(item.distal);
    proximalData.push(item.proximal);
  });

  return { labels, centralData, distalData, proximalData };
}

function TemperatureLineChart({ temperatureData }) {
  const chartData = useMemo(() => {
    if (!temperatureData?.data_points) {
      return { labels: [], centralData: [], distalData: [], proximalData: [] };
    }
    return formatChartData(temperatureData.data_points, temperatureData.range);
  }, [temperatureData]);

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          if (!params || params.length === 0) return "";
          const time = params[0].axisValue;
          let content = `<strong>${time}</strong><br/>`;
          params.forEach((param) => {
            if (param.value !== null && param.value !== undefined) {
              content += `<span style="color:${param.color}">\u25CF</span> ${param.seriesName}: ${param.value}\u00B0C<br/>`;
            }
          });
          return content;
        },
      },
      legend: {
        data: ["Central", "Distal", "Proximal"],
        bottom: 0,
        textStyle: {
          fontSize: 11,
          color: "#666",
        },
      },
      grid: {
        left: 45,
        right: 15,
        top: 20,
        bottom: 50,
      },
      xAxis: {
        type: "category",
        data: chartData.labels,
        axisLabel: {
          fontSize: 10,
          color: "#888",
          interval: "auto",
          rotate: 0,
        },
        axisLine: {
          lineStyle: { color: "#E0E0E0" },
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        min: 20,
        max: 42,
        axisLabel: {
          fontSize: 10,
          color: "#888",
          formatter: "{value}\u00B0",
        },
        splitLine: {
          lineStyle: { color: "#F0F0F0" },
        },
      },
      series: [
        {
          name: "Central",
          type: "line",
          data: chartData.centralData,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            color: TEMP_COLORS.central,
            width: 2,
          },
          itemStyle: {
            color: TEMP_COLORS.central,
          },
          connectNulls: true,
        },
        {
          name: "Distal",
          type: "line",
          data: chartData.distalData,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            color: TEMP_COLORS.distal,
            width: 2,
          },
          itemStyle: {
            color: TEMP_COLORS.distal,
          },
          connectNulls: true,
        },
        {
          name: "Proximal",
          type: "line",
          data: chartData.proximalData,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            color: TEMP_COLORS.proximal,
            width: 2,
          },
          itemStyle: {
            color: TEMP_COLORS.proximal,
          },
          connectNulls: true,
        },
      ],
    }),
    [chartData]
  );

  return (
    <ReactECharts
      option={option}
      style={{ height: 320, width: "100%" }}
      opts={{ renderer: "svg" }}
    />
  );
}

TemperatureLineChart.propTypes = {
  temperatureData: PropTypes.shape({
    range: PropTypes.string,
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string,
        distal: PropTypes.number,
        central: PropTypes.number,
        proximal: PropTypes.number,
        type: PropTypes.string,
      })
    ),
  }),
};

TemperatureLineChart.defaultProps = {
  temperatureData: {
    range: "day",
    data_points: [],
  },
};

export default TemperatureLineChart;
